import * as THREE from 'three';
import type { GameState } from '../state/GameState';
import type { PaintSystem } from './PaintSystem';

export interface SprayHit {
  point: THREE.Vector3;
  distance: number;
  surfaceId: string;
  uv: THREE.Vector2;
}

export interface SpraySettings {
  /** Current paint color as '#rrggbb'. */
  color: string;
  /** 0..1 user-controlled nozzle size (mouse wheel / slider). */
  size: number;
}

const MAX_RANGE = 14;

/**
 * Turns "player is holding the trigger" into StrokeEvents.
 *
 * A screen-center raycast is used in BOTH camera modes, so aiming behaves
 * identically in first and third person. Spray distance shapes the mark the
 * way a real can does: up close it is small, dense, and saturated; farther
 * away it is larger, fainter, and more diffuse. Those values are computed at
 * emit time and stored in the stroke event so replays are exact.
 */
export class SprayController {
  private raycaster = new THREE.Raycaster();
  private lastStampPos: THREE.Vector3 | null = null;
  private lastSurfaceId = '';

  /** Latest aim information, refreshed every frame (for the reticle + particles). */
  aimHit: SprayHit | null = null;
  /** True while actually emitting paint this frame. */
  spraying = false;

  constructor(
    private readonly state: GameState,
    private readonly paintSystem: PaintSystem,
    readonly settings: SpraySettings,
  ) {
    this.raycaster.far = MAX_RANGE * 2;
  }

  /** Raycast from the screen center of the active camera. */
  updateAim(camera: THREE.Camera): void {
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    const hits = this.raycaster.intersectObjects(this.paintSystem.paintableMeshes, false);
    const hit = hits[0];
    if (hit && hit.distance <= MAX_RANGE && hit.uv) {
      this.aimHit = {
        point: hit.point.clone(),
        distance: hit.distance,
        surfaceId: (hit.object.userData.surfaceId as string) ?? '',
        uv: hit.uv.clone(),
      };
    } else {
      this.aimHit = null;
    }
  }

  /** Call every frame with the trigger state; emits stroke events while held. */
  update(painting: boolean, sprayOrigin: THREE.Vector3): void {
    if (!painting || !this.aimHit) {
      this.spraying = false;
      this.lastStampPos = null;
      return;
    }
    this.spraying = true;
    const hit = this.aimHit;
    // Distance from the CAN, not the camera, so third person feels honest.
    const dist = Math.max(sprayOrigin.distanceTo(hit.point), 0.4);

    const baseRadius = 0.12 + this.settings.size * 0.9;
    const radius = baseRadius * (0.45 + dist * 0.18);
    const opacity = 0.9 * THREE.MathUtils.clamp(1.2 / (0.6 + dist * 0.3), 0.12, 1);

    // Stamp continuously along the aim path so fast sweeps don't leave gaps.
    const spacing = radius * 0.4;
    const from =
      this.lastSurfaceId === hit.surfaceId && this.lastStampPos ? this.lastStampPos : null;
    if (from && from.distanceTo(hit.point) > spacing) {
      const steps = Math.min(Math.ceil(from.distanceTo(hit.point) / spacing), 24);
      const p = new THREE.Vector3();
      for (let i = 1; i <= steps; i++) {
        p.lerpVectors(from, hit.point, i / steps);
        this.emit(hit.surfaceId, p, hit.uv, radius, opacity);
      }
    } else {
      this.emit(hit.surfaceId, hit.point, hit.uv, radius, opacity);
    }
    this.lastStampPos = hit.point.clone();
    this.lastSurfaceId = hit.surfaceId;
  }

  private emit(
    surfaceId: string,
    pos: THREE.Vector3,
    uv: THREE.Vector2,
    radius: number,
    opacity: number,
  ): void {
    this.state.strokes.append({
      kind: 'spray',
      locationId: this.state.locationId,
      surfaceId,
      pos: [pos.x, pos.y, pos.z],
      uv: [uv.x, uv.y],
      radius,
      opacity,
      color: this.settings.color,
      seed: Math.floor(Math.random() * 0x7fffffff),
      t: performance.now() - this.state.epoch,
    });
  }
}
